// The Maintenance page, one tab bar. Today: the people to chase, everything
// outstanding with each under their name, and what is ours to do. The other
// tabs are the list: every issue and every compliance renewal, for finding
// things. Reads
// /maintenance.json and re-reads whenever an issue changes: in the slideover
// here, or anywhere else (a colleague, the contractor's portal, Alice) via
// the data-updates channel.

export function maintenanceIndex() {
  return {
    loading: true,
    today: { count: 0, landlords: [], contractors: [], to_do: [] },
    list: { rows: [], counts: {} },
    activeTab: 'today',
    sortKey: 'raised',
    sortDir: 'desc',
    searchQuery: '',
    searching: false,
    hasMore: false,
    showBackToTop: false,
    _requestId: 0,
    _liveTimer: null,

    init() {
      this.load()
      // ?open_issue= / ?open_renewal= from a link into the page
      const params = new URLSearchParams(window.location.search)
      if (params.get('open_issue')) window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id: parseInt(params.get('open_issue')) } }))
      else if (params.get('open_renewal')) window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { renewal_id: parseInt(params.get('open_renewal')) } }))
      // The slideovers say when something changed; the page follows
      window.addEventListener('issue-changed', () => this.load(true))
      window.addEventListener('job-updated', () => this.load(true))
      window.addEventListener('linked-send-completed', () => this.load(true))
      window.addEventListener('focus', () => this.load(true))
    },

    // A change on the channel from anywhere. One save commits several
    // records (an issue cancelled withdraws its jobs), so the re-read waits
    // for the burst to end
    handleMaintenanceUpdate() {
      clearTimeout(this._liveTimer)
      this._liveTimer = setTimeout(() => this.load(true), 400)
    },

    setTab(key) { this.activeTab = key },

    get dateHeading() {
      return { open: 'Open for', done: 'Done', cancelled: 'Cancelled', compliance: 'Due' }[this.activeTab] || 'Date'
    },

    // Open rows say how long they have been open; finished rows say when
    dateCell(row) {
      if (this.activeTab === 'compliance') return row.due_display || '—'
      if (row.days_open == null) return row.date_display || ''
      return row.days_open === 0 ? 'today' : `${row.days_open} ${row.days_open === 1 ? 'day' : 'days'}`
    },

    dateTone(row) {
      if (this.activeTab === 'compliance') return row.overdue ? 'text-[#B45309]' : 'text-[#9CA3AF]'
      return row.tone === 'late' ? 'text-[#B45309]' : 'text-[#9CA3AF]'
    },

    get tabs() {
      return [ { key: 'today', label: 'Today' }, { key: 'open', label: 'Open' }, { key: 'done', label: 'Done' }, { key: 'cancelled', label: 'Cancelled' }, { key: 'compliance', label: 'Compliance' } ]
    },

    tabCount(key) {
      return key === 'today' ? this.today.count : (this.list.counts[key] ?? 0)
    },

    get todayEmpty() {
      return !this.today.landlords.length && !this.today.contractors.length && !this.today.to_do.length
    },

    get visibleRows() {
      const rows = this.activeTab === 'compliance' ? this.list.rows.filter(r => r.compliance && r.status === 'open') : this.list.rows.filter(r => r.status === this.activeTab)
      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortKey
      return [...rows].sort((a, b) => {
        if (key === 'contractor') {
          // Alphabetical by the person, the uninstructed last whichever way
          if (!a.contractor && !b.contractor) return 0
          if (!a.contractor) return 1
          if (!b.contractor) return -1
          return dir * (a.contractor.localeCompare(b.contractor) || (a.firm || '').localeCompare(b.firm || ''))
        }
        if (key === 'date' && this.activeTab === 'compliance') {
          const av = a.due ? Date.parse(a.due) : Infinity
          const bv = b.due ? Date.parse(b.due) : Infinity
          return dir * (av - bv)
        }
        if (key === 'date') {
          // Open rows by how long they have been open; finished rows by when
          const av = a.days_open != null ? a.days_open : -Date.parse(a.date || 0) / 86400000
          const bv = b.days_open != null ? b.days_open : -Date.parse(b.date || 0) / 86400000
          return dir * (av - bv)
        }
        return dir * (Date.parse(a.raised || 0) - Date.parse(b.raised || 0))
      })
    },

    // Click a heading to sort by it; click again to flip
    sortBy(key) {
      if (this.sortKey === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; return }
      this.sortKey = key
      this.sortDir = key === 'contractor' || (key === 'date' && this.activeTab === 'compliance') ? 'asc' : 'desc'
    },

    sortIcon(key) {
      if (this.sortKey !== key) return ''
      return this.sortDir === 'asc' ? 'ph-caret-up' : 'ph-caret-down'
    },

    get totalCount() { return this.list.rows.length },

    openIssue(id) {
      window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id } }))
    },

    // A renewal opens in the same slideover, read off its job
    openRow(row) {
      if (row.kind === 'renewal') {
        window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { renewal_id: row.id } }))
        return
      }
      this.openIssue(row.id)
    },

    // A Today item is an issue or a renewal
    openItem(item) {
      this.openRow(item)
    },

    // The item's verb opens the issue on the right form: log the answer,
    // set the date, mark it done
    runItemVerb(item) {
      if (!item.verb) return
      const detail = item.kind === 'renewal' ? { renewal_id: item.id, verb: item.verb.action } : { id: item.id, verb: item.verb.action }
      window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail }))
    },

    emailPerson(person) {
      const c = person.contact || {}
      if (!c.email) return
      if (typeof window.openEmail === 'function') window.openEmail(c.email, { name: person.name, type: (c.type || '').toLowerCase(), id: c.id })
      else window.location.href = `mailto:${c.email}`
    },


    toneClass(tone) {
      return { late: 'text-[#B45309]', done: 'text-[#047857]', off: 'text-[#9CA3AF]' }[tone] || 'text-[#374151]'
    },

    search() { this.load() },

    async load(silent = false) {
      const id = ++this._requestId
      if (!silent) this.loading = true
      this.searching = !!this.searchQuery
      try {
        const q = this.searchQuery.trim()
        const response = await fetch(`/maintenance.json${q ? '?q=' + encodeURIComponent(q) : ''}`, { headers: { 'Accept': 'application/json' } })
        const data = await response.json()
        if (id !== this._requestId) return
        this.today = data.today || { count: 0, landlords: [], contractors: [], to_do: [] }
        this.list = data.list || { rows: [], counts: {} }
        // A search is a find: show the list
        if (q && this.activeTab === 'today') this.activeTab = 'open'
      } catch (e) {
        console.error('Maintenance load failed:', e)
      } finally {
        if (id === this._requestId) { this.loading = false; this.searching = false }
      }
    },

    handleScroll() {},
    scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) }
  }
}
